/**
 * UniversalChakraRenderer - Generic D3 renderer for ALL chakra types
 *
 * Key Principles:
 * 1. ZERO BUSINESS LOGIC - only rendering
 * 2. DATA-DRIVEN - all decisions made by data
 * 3. COMPOSABLE - labels/styling via data config
 * 4. REUSABLE - works for Muhurta, Nakshatra, D7-D60, any future chakras
 *
 * Usage:
 * ```typescript
 * const renderer = new UniversalChakraRenderer(chartId, chakraType, layer);
 * renderer.renderFromData(chakraRenderData); // Data includes everything!
 * renderer.show(); // or hide()
 * ```
 *
 * Architecture: Layer-Based Universal v4.0.0
 * Replaces: MuhurtaChakraDivisionsRenderer, NakshatraChakraDivisionsRenderer,
 *           DivisionalChakraRenderer, AshtamshaChakraDivisionsRenderer
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import * as d3 from 'd3';
import { ElementRenderingEngine } from '../engines/ElementRenderingEngine';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';
import { FONTS } from '../../../../../config/fonts';
import { getChakraDimensions } from '../../../../../config/chakraDimensionsConfig';
import { coordinateToDecimal, parseDMSToDecimal } from '../../../../../utils/astrology/coordinates';
import { Coordinate } from '../../../../../models/types';
import { ChakraDataService } from '../../../../../services/ChakraDataService';
import { globalEventStream } from '../core/EventStream';
import {
  UniversalChakraRenderData,
  UniversalChakraRendererConfig,
  ChakraSector,
  ChakraLabel,
  ChakraVisualConfig,
  DivisionLine
} from '../../../../../types/UniversalChakraTypes';
import { LabelRenderer } from '../renderers/LabelRenderer';
import {
  ChakraLabel as PositionLabel,
  LabelStyle
} from '../types/LabelTypes';

/**
 * Universal Chakra Renderer - works for ALL chakra types
 */
export class UniversalChakraRenderer {
  private chartId: string;
  private chakraType: string;
  private layer: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  private config: UniversalChakraRendererConfig;
  private elementRenderingEngine: ElementRenderingEngine;
  private isVisible: boolean = false;
  private startPointLongitude: number = 0;
  private currentRenderData: UniversalChakraRenderData | null = null;
  private eventSubscriptions: Array<() => void> = [];

  constructor(
    chartId: string,
    chakraType: string,
    layer: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    config?: Partial<UniversalChakraRendererConfig>
  ) {
    this.chartId = chartId;
    this.chakraType = chakraType;
    this.layer = layer;
    this.config = {
      chartId,
      chakraType,
      debugMode: config?.debugMode || false,
      defaultVisual: config?.defaultVisual
    };

    // Get singleton rendering engine
    this.elementRenderingEngine = ElementRenderingEngine.getInstance();

    // Subscribe to subdivision toggle events (e.g., Pada toggle for Nakshatra)
    this.subscribeToSubdivisionEvents();

    if (this.config.debugMode) {
      devLog(`🎨 UniversalChakraRenderer[${chartId}][${chakraType}]: Initialized`);
    }
  }

  /**
   * Main render method - renders from ready-to-render data
   * NO LOGIC - just draws what data tells it to draw
   */
  public renderFromData(data: UniversalChakraRenderData): void {
    if (!this.layer) {
      devWarn(`⚠️ UniversalChakraRenderer[${this.chakraType}]: Layer not initialized`);
      return;
    }

    const startTime = performance.now();

    // Save current render data for re-rendering with subdivisions
    this.currentRenderData = data;

    // Parse start point longitude
    this.startPointLongitude = parseDMSToDecimal(data.startPointLongitude);

    // Clear existing content
    this.clear();

    // Create main group
    const mainGroup = this.layer
      .append('g')
      .attr('class', `${this.chakraType}-chakra-group`)
      .attr('data-chakra-type', this.chakraType)
      .attr('data-chart-id', this.chartId)
      .attr('data-divisions', data.divisions);

    // Add metadata as data attributes
    if (data.metadata) {
      Object.entries(data.metadata).forEach(([key, value]) => {
        mainGroup.attr(`data-${key}`, String(value));
      });
    }

    // Get visual config (data overrides defaults)
    const visualConfig = {
      ...this.getDefaultVisualConfig(),
      ...this.config.defaultVisual,
      ...data.visual
    };

    // Render sectors
    this.renderSectors(mainGroup, data.sectors, visualConfig);

    // Render division lines (e.g., Pada divisions)
    if (data.divisionLines && data.divisionLines.length > 0) {
      this.renderDivisionLines(mainGroup, data.divisionLines, visualConfig);
    }

    // Render inner labels (e.g., Pada numbers)
    if (data.innerLabels && data.innerLabels.length > 0) {
      this.renderInnerLabels(mainGroup, data.innerLabels);
    }

    // Render position labels (e.g., "Ml" for Muhurta longitude)
    if (data.positionLabels && data.positionLabels.length > 0) {
      this.renderPositionLabels(mainGroup, data.positionLabels);
    }

    const renderTime = performance.now() - startTime;
    if (this.config.debugMode) {
      devLog(
        `✅ UniversalChakraRenderer[${this.chartId}][${this.chakraType}]: ` +
        `Rendered ${data.sectors.length} sectors` +
        `${data.divisionLines ? ` + ${data.divisionLines.length} division lines` : ''}` +
        `${data.innerLabels ? ` + ${data.innerLabels.length} inner labels` : ''}` +
        `${data.positionLabels ? ` + ${data.positionLabels.length} position labels` : ''} ` +
        `in ${renderTime.toFixed(2)}ms`
      );
    }

    this.isVisible = false; // Hidden by default
  }

  /**
   * Render all sectors
   */
  private renderSectors(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    sectors: ChakraSector[],
    visualConfig: ChakraVisualConfig
  ): void {
    const colors = visualConfig.sectorFillColors || [];

    sectors.forEach((sector, index) => {
      // Determine color (per-sector or from pattern)
      const fillColor = sector.fillColor || colors[index % colors.length] || '#cccccc';
      const strokeColor = sector.strokeColor || fillColor;

      // Create sector path
      const sectorData = this.createSectorPath(sector.startDegree, sector.endDegree);

      if (sectorData) {
        // Create sector element
        const sectorElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        sectorElement.setAttribute('d', sectorData.pathData);
        sectorElement.setAttribute('fill', fillColor);
        sectorElement.setAttribute('stroke', strokeColor);
        sectorElement.setAttribute('stroke-width', (visualConfig.sectorStrokeWidth ?? 0.5).toString());
        sectorElement.setAttribute('fill-opacity', (visualConfig.sectorFillOpacity ?? 0.15).toString());
        sectorElement.setAttribute('stroke-opacity', (visualConfig.sectorStrokeOpacity ?? 0.3).toString());
        sectorElement.setAttribute('class', `${this.chakraType}-sector sector-${sector.index}`);
        sectorElement.setAttribute('data-sector-index', sector.index.toString());
        sectorElement.setAttribute('data-start-degree', sector.startDegree.toString());
        sectorElement.setAttribute('data-end-degree', sector.endDegree.toString());

        // Add metadata
        if (sector.metadata) {
          Object.entries(sector.metadata).forEach(([key, value]) => {
            sectorElement.setAttribute(`data-${key}`, String(value));
          });
        }

        group.node()!.appendChild(sectorElement);

        // Render labels for this sector
        if (sector.labels && sector.labels.length > 0) {
          const sectorCenterDegree = this.calculateSectorCenter(sector.startDegree, sector.endDegree);
          sector.labels.forEach(label => {
            this.renderLabel(group, sectorCenterDegree, label, sector.index, sector.startDegree, sector.endDegree);
          });
        }
      }
    });
  }

  /**
   * Render division lines (e.g., Pada divisions in Nakshatra)
   */
  private renderDivisionLines(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    divisionLines: any[],
    visualConfig: ChakraVisualConfig
  ): void {
    const defaultStyle = visualConfig.divisionLineStyle || {
      stroke: '#666666',
      strokeWidth: 0.5,
      strokeDasharray: '2,2',
      strokeOpacity: 0.5
    };

    divisionLines.forEach((divLine, index) => {
      const containerIndex = this.getContainerIndexForGlobalDegree(divLine.degree);
      const localDegree = this.getLocalDegreeFromGlobal(divLine.degree);

      // Get center line position
      const centerPosition = this.elementRenderingEngine.calculatePlanetPositionForAnimation(
        containerIndex,
        localDegree
      );

      if (!centerPosition) return;

      // Calculate perpendicular positions for line
      const perpPositions = this.calculateCustomPerpendicularPositions(
        containerIndex,
        centerPosition,
        localDegree,
        divLine.length
      );

      if (!perpPositions) return;

      // Merge styles
      const style = {
        stroke: divLine.style?.stroke ?? defaultStyle.stroke,
        strokeWidth: divLine.style?.strokeWidth ?? defaultStyle.strokeWidth,
        strokeDasharray: divLine.style?.strokeDasharray ?? defaultStyle.strokeDasharray,
        strokeOpacity: divLine.style?.strokeOpacity ?? defaultStyle.strokeOpacity
      };

      // Create line element (from center to outer edge)
      const lineElement = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      lineElement.setAttribute('x1', centerPosition.x.toString());
      lineElement.setAttribute('y1', centerPosition.y.toString());
      lineElement.setAttribute('x2', perpPositions.pos2.x.toString());
      lineElement.setAttribute('y2', perpPositions.pos2.y.toString());
      lineElement.setAttribute('stroke', style.stroke);
      lineElement.setAttribute('stroke-width', style.strokeWidth.toString());
      lineElement.setAttribute('stroke-dasharray', style.strokeDasharray);
      lineElement.setAttribute('stroke-opacity', style.strokeOpacity.toString());
      lineElement.setAttribute('class', `${this.chakraType}-division-line`);
      lineElement.setAttribute('data-line-index', index.toString());

      // Add metadata
      if (divLine.metadata) {
        Object.entries(divLine.metadata).forEach(([key, value]) => {
          lineElement.setAttribute(`data-${key}`, String(value));
        });
      }

      group.node()!.appendChild(lineElement);
    });
  }

  /**
   * Render inner labels (e.g., Pada numbers)
   */
  private renderInnerLabels(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    innerLabels: any[]
  ): void {
    const preset = chartColorPresetManager.getCurrentPreset();
    const defaultStyle = preset.muhurtaDivisions?.sectorNumberStyle || {
      fontFamily: FONTS.chart.primary,
      fontSize: 9,
      fontWeight: 'normal',
      fill: '#666666',
      stroke: '#ffffff',
      strokeWidth: 0.3
    };

    innerLabels.forEach((label, index) => {
      const containerIndex = this.getContainerIndexForGlobalDegree(label.degree);
      const localDegree = this.getLocalDegreeFromGlobal(label.degree);

      // Get center position
      const centerPosition = this.elementRenderingEngine.calculatePlanetPositionForAnimation(
        containerIndex,
        localDegree
      );

      if (!centerPosition) return;

      // Calculate label position with offset
      const labelPosition = this.calculateOffsetFromCenterLine(
        containerIndex,
        centerPosition,
        localDegree,
        label.offset
      );

      if (!labelPosition) return;

      // Merge styles
      const style = {
        fontFamily: label.style?.fontFamily ?? defaultStyle.fontFamily,
        fontSize: label.style?.fontSize ?? defaultStyle.fontSize,
        fontWeight: label.style?.fontWeight ?? defaultStyle.fontWeight,
        fill: label.style?.fill ?? defaultStyle.fill,
        stroke: label.style?.stroke ?? defaultStyle.stroke,
        strokeWidth: label.style?.strokeWidth ?? defaultStyle.strokeWidth,
        opacity: label.style?.opacity ?? 1
      };

      // Create text element
      const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      textElement.setAttribute('x', labelPosition.x.toString());
      textElement.setAttribute('y', labelPosition.y.toString());
      textElement.setAttribute('text-anchor', 'middle');
      textElement.setAttribute('dominant-baseline', 'central');
      textElement.setAttribute('font-family', style.fontFamily);
      textElement.setAttribute('font-size', style.fontSize.toString());
      textElement.setAttribute('font-weight', style.fontWeight);
      textElement.setAttribute('fill', style.fill);
      textElement.setAttribute('stroke', style.stroke);
      textElement.setAttribute('stroke-width', style.strokeWidth.toString());
      textElement.setAttribute('opacity', style.opacity.toString());
      textElement.setAttribute('class', `${this.chakraType}-inner-label`);
      textElement.setAttribute('data-label-index', index.toString());
      textElement.textContent = label.content;

      // Add metadata
      if (label.metadata) {
        Object.entries(label.metadata).forEach(([key, value]) => {
          textElement.setAttribute(`data-${key}`, String(value));
        });
      }

      group.node()!.appendChild(textElement);
    });
  }

  /**
   * Render a single label - PURE RENDERING, no logic
   */
  private renderLabel(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    sectorCenterDegree: number,
    label: ChakraLabel,
    sectorIndex: number,
    sectorStartDegree?: number,
    sectorEndDegree?: number
  ): void {
    // Check if curved label
    if (label.curved) {
      this.renderCurvedLabel(group, sectorCenterDegree, label, sectorIndex, sectorStartDegree, sectorEndDegree);
      return;
    }

    // Standard straight label rendering
    // Get position in chart
    const containerIndex = this.getContainerIndexForGlobalDegree(sectorCenterDegree);
    const localDegree = this.getLocalDegreeFromGlobal(sectorCenterDegree);

    // Calculate position
    const basePosition = this.elementRenderingEngine.calculatePlanetPositionForAnimation(
      containerIndex,
      localDegree
    );

    if (!basePosition) return;

    // Apply offset
    const labelPosition = this.calculateOffsetFromCenterLine(
      containerIndex,
      basePosition,
      localDegree,
      label.offset
    );

    if (!labelPosition) return;

    // Get default style from preset (use rulerCodeStyle for outer labels, sectorNumberStyle for inner)
    const preset = chartColorPresetManager.getCurrentPreset();
    const isOuterLabel = label.position === 'outer';
    const defaultStyle = isOuterLabel
      ? (preset.muhurtaDivisions?.rulerCodeStyle || {})
      : (preset.muhurtaDivisions?.sectorNumberStyle || {});

    // Merge styles (label style overrides defaults) - use ?? for numeric values that can be 0
    const style = {
      fontFamily: label.style?.fontFamily ?? defaultStyle.fontFamily ?? FONTS.chart.primary,
      fontSize: label.style?.fontSize ?? defaultStyle.fontSize ?? 10,
      fontWeight: label.style?.fontWeight ?? defaultStyle.fontWeight ?? 'bold',
      fill: label.style?.fill ?? defaultStyle.fill ?? '#333333',
      stroke: label.style?.stroke ?? defaultStyle.stroke ?? '#ffffff',
      strokeWidth: label.style?.strokeWidth ?? defaultStyle.strokeWidth ?? 0.5,
      opacity: label.style?.opacity ?? 1
    };

    // Create text element
    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('x', labelPosition.x.toString());
    textElement.setAttribute('y', labelPosition.y.toString());
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'central');
    textElement.setAttribute('font-family', style.fontFamily);
    textElement.setAttribute('font-size', style.fontSize.toString());
    textElement.setAttribute('font-weight', style.fontWeight);
    textElement.setAttribute('fill', style.fill);
    textElement.setAttribute('stroke', style.stroke);
    textElement.setAttribute('stroke-width', style.strokeWidth.toString());
    textElement.setAttribute('opacity', style.opacity.toString());
    textElement.setAttribute('class', label.className || `${this.chakraType}-label`);
    textElement.setAttribute('data-sector-index', sectorIndex.toString());
    textElement.setAttribute('data-label-position', label.position);
    textElement.textContent = label.content;

    group.node()!.appendChild(textElement);
  }

  /**
   * Helper: Create SVG path for sector with boundary points
   * Based on MuhurtaChakraDivisionsRenderer approach
   */
  private createSectorPath(startDegree: number, endDegree: number): {
    pathData: string;
    outerBoundaryPoints: { x: number; y: number }[];
    innerBoundaryPoints: { x: number; y: number }[];
    midPoint: { x: number; y: number };
  } | null {
    // IMPORTANT: sector.startDegree and sector.endDegree already include the start point offset!
    // They come from generateUniversalDivisionalSectors which adds startPointOffset to each sector.
    // So we use them directly without subtracting this.startPointLongitude
    const adjustedStart = (startDegree + 360) % 360;
    const adjustedEnd = (endDegree + 360) % 360;

    // Get sector width from centralized dimensions config
    const dimensions = getChakraDimensions(this.chakraType);
    const sectorWidth = dimensions.sectorWidth;

    // Handle case where sector crosses 360°→0° boundary
    const sectorCrossesBoundary = adjustedEnd < adjustedStart;

    // Collect all points for the sector path and labels
    const outerPoints: { x: number; y: number }[] = [];
    const innerPoints: { x: number; y: number }[] = [];

    // Iterate through each degree in the sector (use 0.5° steps for smoother paths)
    const step = 0.5;

    if (sectorCrossesBoundary) {
      // Sector crosses 360°→0° boundary
      for (let currentDegree = adjustedStart; currentDegree < 360; currentDegree += step) {
        this.addSectorPointsForDegree(currentDegree, sectorWidth, outerPoints, innerPoints);
      }
      for (let currentDegree = 0; currentDegree <= adjustedEnd; currentDegree += step) {
        this.addSectorPointsForDegree(currentDegree, sectorWidth, outerPoints, innerPoints);
      }
      if (adjustedEnd > 0) {
        this.addSectorPointsForDegree(adjustedEnd, sectorWidth, outerPoints, innerPoints);
      }
    } else {
      // Normal case: sector doesn't cross boundary
      for (let currentDegree = adjustedStart; currentDegree <= adjustedEnd; currentDegree += step) {
        this.addSectorPointsForDegree(currentDegree, sectorWidth, outerPoints, innerPoints);
      }
      this.addSectorPointsForDegree(adjustedEnd, sectorWidth, outerPoints, innerPoints);
    }

    // Create path from collected points
    if (outerPoints.length > 0 && innerPoints.length > 0) {
      let pathData = `M ${outerPoints[0].x} ${outerPoints[0].y}`;

      // Draw along outer edge
      for (let i = 1; i < outerPoints.length; i++) {
        pathData += ` L ${outerPoints[i].x} ${outerPoints[i].y}`;
      }

      // Draw to end of inner edge
      pathData += ` L ${innerPoints[innerPoints.length - 1].x} ${innerPoints[innerPoints.length - 1].y}`;

      // Draw back along inner edge (reversed)
      for (let i = innerPoints.length - 2; i >= 0; i--) {
        pathData += ` L ${innerPoints[i].x} ${innerPoints[i].y}`;
      }

      pathData += ' Z'; // Close the path

      // Calculate midpoint for label positioning
      const midIndex = Math.floor(outerPoints.length / 2);
      const midPoint = outerPoints[midIndex];

      return {
        pathData,
        outerBoundaryPoints: outerPoints,
        innerBoundaryPoints: innerPoints,
        midPoint
      };
    }

    return null;
  }

  /**
   * Helper method to add points for a specific degree to sector path
   */
  private addSectorPointsForDegree(
    currentDegree: number,
    sectorWidth: number,
    outerPoints: { x: number; y: number }[],
    innerPoints: { x: number; y: number }[]
  ): void {
    const containerIndex = this.getContainerIndexForGlobalDegree(currentDegree);
    const localDegree = this.getLocalDegreeFromGlobal(currentDegree);

    // Get the center line position
    const centerPos = this.elementRenderingEngine.calculatePlanetPositionForAnimation(
      containerIndex,
      localDegree
    );

    if (centerPos) {
      // Use D8's approach: pass FULL sectorWidth to calculateCustomPerpendicularPositions
      // It will divide by 2 internally to create positions at ±sectorWidth/2
      const customPositions = this.calculateCustomPerpendicularPositions(
        containerIndex,
        centerPos,
        localDegree,
        sectorWidth  // Full width, not divided by 2!
      );

      if (customPositions) {
        innerPoints.push(customPositions.pos1);  // pos1 is at -sectorWidth/2
        outerPoints.push(customPositions.pos2);  // pos2 is at +sectorWidth/2
      }
    }
  }

  /**
   * Helper: Calculate sector center degree
   */
  private calculateSectorCenter(startDegree: number, endDegree: number): number {
    if (endDegree < startDegree) {
      // Crosses 0°
      const center = (startDegree + (endDegree + 360)) / 2;
      return center >= 360 ? center - 360 : center;
    }
    return (startDegree + endDegree) / 2;
  }

  /**
   * Helper: Get container index for global degree
   */
  private getContainerIndexForGlobalDegree(globalDegree: number): number {
    const rasiIndex = Math.floor(globalDegree / 30) % 12;
    return rasiIndex === 11 ? 0 : rasiIndex + 1;
  }

  /**
   * Helper: Get local degree from global
   */
  private getLocalDegreeFromGlobal(globalDegree: number): number {
    return globalDegree % 30;
  }

  /**
   * Calculate custom perpendicular positions with exact length
   */
  private calculateCustomPerpendicularPositions(
    containerIndex: number,
    position: { x: number; y: number },
    localDegree: number,
    desiredLength: number
  ): { pos1: { x: number; y: number }; pos2: { x: number; y: number } } | null {
    // Get direction from ElementRenderingEngine (but ignore its length)
    const tempPos1 = this.elementRenderingEngine.calculatePerpendicularPositionForAnimation(
      containerIndex,
      position,
      localDegree,
      0
    );
    const tempPos2 = this.elementRenderingEngine.calculatePerpendicularPositionForAnimation(
      containerIndex,
      position,
      localDegree,
      1
    );

    if (!tempPos1 || !tempPos2) {
      return null;
    }

    // Calculate direction vector (normalized)
    const dirX = tempPos2.x - tempPos1.x;
    const dirY = tempPos2.y - tempPos1.y;
    const currentLength = Math.sqrt(dirX * dirX + dirY * dirY);

    if (currentLength === 0) {
      return null;
    }

    // Normalize and scale to desired length
    const normalizedDirX = dirX / currentLength;
    const normalizedDirY = dirY / currentLength;
    const halfDesiredLength = desiredLength / 2;

    // Create new positions with exact desired length
    const pos1 = {
      x: position.x - normalizedDirX * halfDesiredLength,
      y: position.y - normalizedDirY * halfDesiredLength
    };
    const pos2 = {
      x: position.x + normalizedDirX * halfDesiredLength,
      y: position.y + normalizedDirY * halfDesiredLength
    };

    return { pos1, pos2 };
  }

  /**
   * Calculate offset from center line (same logic as D8)
   */
  private calculateOffsetFromCenterLine(
    containerIndex: number,
    centerLinePosition: { x: number; y: number },
    localDegree: number,
    distanceFromCenter: number
  ): { x: number; y: number } | null {
    const perpendicularPositions = this.calculateCustomPerpendicularPositions(
      containerIndex,
      centerLinePosition,
      localDegree,
      distanceFromCenter
    );

    if (perpendicularPositions) {
      // Use pos2 (outer position) for positive distance, pos1 (inner) for negative
      // Same simple logic as D8 - no distance calculation to chart center
      return distanceFromCenter > 0 ? perpendicularPositions.pos2 : perpendicularPositions.pos1;
    }

    return null;
  }


  /**
   * Get default visual configuration
   * Uses same configuration as D8/Muhurta chakras for consistency
   */
  private getDefaultVisualConfig(): ChakraVisualConfig {
    const preset = chartColorPresetManager.getCurrentPreset();
    const muhurtaConfig = preset.muhurtaDivisions;

    return {
      sectorFillColors: muhurtaConfig?.sectorFillColors ?? ['#4A90E2', '#E94B3C'],
      sectorStrokeWidth: muhurtaConfig?.sectorStrokeWidth ?? 0.5,
      sectorFillOpacity: muhurtaConfig?.sectorFillOpacity ?? 0.15,
      sectorStrokeOpacity: muhurtaConfig?.sectorStrokeOpacity ?? 0.3,
      showDivisionLines: false
    };
  }

  /**
   * Visibility control
   */
  public show(): void {
    if (this.layer) {
      this.layer.style('display', null);
      this.isVisible = true;
    }
  }

  public hide(): void {
    if (this.layer) {
      this.layer.style('display', 'none');
      this.isVisible = false;
    }
  }

  /**
   * Render position labels (e.g., "Ml" for Muhurta chakra longitude)
   * Uses LabelRenderer for flexible, styled label rendering
   */
  private renderPositionLabels(
    parentGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    labels: Array<{ name: string; longitude: number; styleType?: string; color?: string }>
  ): void {
    const preset = chartColorPresetManager.getCurrentPreset();

    // Convert data labels to PositionLabel format
    const positionLabels: PositionLabel[] = labels.map(label => ({
      name: label.name,
      longitude: label.longitude,
      styleType: label.styleType,
      color: label.color
    }));

    // Helper to get style for a label
    const getStyle = (label: PositionLabel): LabelStyle => {
      const styleType = label.styleType || 'default';
      const presetStyle = preset.labelStyles[styleType as keyof typeof preset.labelStyles] || preset.labelStyles.default;

      return {
        type: styleType,
        text: presetStyle.text,
        line: presetStyle.line,
        background: presetStyle.background,
        labelOffset: presetStyle.labelOffset
      };
    };

    // Helper to calculate position for a label
    const calculatePosition = (label: PositionLabel) => {
      const containerIndex = this.getContainerIndexForGlobalDegree(label.longitude);
      const localDegree = this.getLocalDegreeFromGlobal(label.longitude);

      // Get center line position
      const centerPosition = this.elementRenderingEngine.calculatePlanetPositionForAnimation(
        containerIndex,
        localDegree
      );

      if (!centerPosition) {
        return null;
      }

      // Get label style to determine offset
      const style = getStyle(label);

      // Calculate label position offset from center line
      const labelPosition = this.calculateOffsetFromCenterLine(
        containerIndex,
        centerPosition,
        localDegree,
        style.labelOffset
      );

      if (!labelPosition) {
        return null;
      }

      return {
        containerIndex,
        localDegree,
        centerPosition,
        labelPosition
      };
    };

    // Render all labels using LabelRenderer
    LabelRenderer.renderLabels(
      positionLabels,
      parentGroup,
      getStyle,
      calculatePosition
    );

    if (this.config.debugMode) {
      devLog(
        `🏷️ UniversalChakraRenderer[${this.chakraType}]: ` +
        `Rendered ${positionLabels.length} position labels`
      );
    }
  }

  /**
   * Clear all rendered content
   */
  public clear(): void {
    if (this.layer) {
      this.layer.selectAll('*').remove();
    }
  }

  /**
   * Update only the position label text (optimized - no full re-render)
   * Used when only custom label name changes, degrees stay the same
   */
  public updatePositionLabelText(newLabel: string): void {
    if (!this.layer || !this.currentRenderData) return;

    // Find and update the position label text element
    const mainGroup = this.layer.select(`.chakra-main-${this.chakraType}`);
    if (mainGroup.empty()) return;

    // Update text in the label group
    const labelText = mainGroup.select('.label-text');
    if (!labelText.empty()) {
      labelText.text(newLabel);
    }

    // Also update currentRenderData for consistency
    if (this.currentRenderData.positionLabels && this.currentRenderData.positionLabels.length > 0) {
      this.currentRenderData.positionLabels[0].name = newLabel;
    }
  }

  /**
   * Render curved label along sector path
   * Based on NakshatraChakraDivisionsRenderer curved text logic
   */
  private renderCurvedLabel(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    sectorCenterDegree: number,
    label: ChakraLabel,
    sectorIndex: number,
    sectorStartDegree?: number,
    sectorEndDegree?: number
  ): void {
    const preset = chartColorPresetManager.getCurrentPreset();
    const curvedDistance = label.curvedDistance || 25; // Default distance for curved path

    // Get default style
    const defaultStyle = preset.muhurtaDivisions?.rulerCodeStyle || {};
    const style = {
      fontFamily: label.style?.fontFamily ?? defaultStyle.fontFamily ?? FONTS.chart.primary,
      fontSize: label.style?.fontSize ?? defaultStyle.fontSize ?? 10,
      fontWeight: label.style?.fontWeight ?? defaultStyle.fontWeight ?? 'bold',
      fill: label.style?.fill ?? defaultStyle.fill ?? '#333333',
      stroke: label.style?.stroke ?? defaultStyle.stroke ?? '#ffffff',
      strokeWidth: label.style?.strokeWidth ?? defaultStyle.strokeWidth ?? 0.5,
      opacity: label.style?.opacity ?? 1
    };

    // Use real sector boundaries if provided, otherwise fallback to approximation
    let startDegree: number;
    let endDegree: number;

    if (sectorStartDegree !== undefined && sectorEndDegree !== undefined) {
      // Use real sector boundaries (CORRECT approach from old NakshatraChakraDivisionsRenderer)
      startDegree = sectorStartDegree;
      endDegree = sectorEndDegree;
    } else {
      // Fallback: approximate sector span for backward compatibility
      const sectorSpanHalf = 6.666;
      startDegree = sectorCenterDegree - sectorSpanHalf;
      endDegree = sectorCenterDegree + sectorSpanHalf;
    }

    // Check if two-line rendering needed
    if (label.twoLine && label.content.includes(' ')) {
      this.renderTwoLineCurvedText(group, startDegree, endDegree, sectorIndex, label, curvedDistance, style);
    } else {
      this.renderSingleLineCurvedText(group, startDegree, endDegree, sectorIndex, label, curvedDistance, style);
    }
  }

  /**
   * Render single line curved text along path
   */
  private renderSingleLineCurvedText(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    startDegree: number,
    endDegree: number,
    sectorIndex: number,
    label: ChakraLabel,
    curvedDistance: number,
    style: any
  ): void {
    const pathId = `${this.chakraType}-curve-${this.chartId}-${sectorIndex}`;
    const pathPoints = this.generateCurvedPathPoints(startDegree, endDegree, curvedDistance);

    if (pathPoints.length < 2) return;

    // Create invisible path for text to follow
    const pathData = this.createPathDataFromPoints(pathPoints);
    this.createInvisiblePath(group, pathId, pathData);

    // Create curved text element
    this.createCurvedTextElement(group, pathId, label.content, style, 1.0);
  }

  /**
   * Render two-line curved text (for long names like "Purva Bhadrapada")
   */
  private renderTwoLineCurvedText(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    startDegree: number,
    endDegree: number,
    sectorIndex: number,
    label: ChakraLabel,
    curvedDistance: number,
    style: any
  ): void {
    const parts = label.content.split(' ');
    if (parts.length < 2) {
      // Fallback to single line if no space
      this.renderSingleLineCurvedText(group, startDegree, endDegree, sectorIndex, label, curvedDistance, style);
      return;
    }

    const upperText = parts[0];
    const lowerText = parts.slice(1).join(' ');
    const lineSpacing = 8;

    // Upper line
    const upperPathId = `${this.chakraType}-curve-upper-${this.chartId}-${sectorIndex}`;
    const upperPathPoints = this.generateCurvedPathPoints(startDegree, endDegree, curvedDistance + lineSpacing);
    if (upperPathPoints.length >= 2) {
      const upperPathData = this.createPathDataFromPoints(upperPathPoints);
      this.createInvisiblePath(group, upperPathId, upperPathData);
      this.createCurvedTextElement(group, upperPathId, upperText, style, 0.8);
    }

    // Lower line
    const lowerPathId = `${this.chakraType}-curve-lower-${this.chartId}-${sectorIndex}`;
    const lowerPathPoints = this.generateCurvedPathPoints(startDegree, endDegree, curvedDistance - lineSpacing);
    if (lowerPathPoints.length >= 2) {
      const lowerPathData = this.createPathDataFromPoints(lowerPathPoints);
      this.createInvisiblePath(group, lowerPathId, lowerPathData);
      this.createCurvedTextElement(group, lowerPathId, lowerText, style, 0.8);
    }
  }

  /**
   * Generate curved path points along outer edge
   */
  private generateCurvedPathPoints(
    startDegree: number,
    endDegree: number,
    distance: number
  ): Array<{ x: number; y: number; degree: number }> {
    const sectorCrossesBoundary = endDegree < startDegree;
    const sectorSpan = sectorCrossesBoundary ? (360 - startDegree + endDegree) : (endDegree - startDegree);
    const pathPoints: Array<{ x: number; y: number; degree: number }> = [];
    const pointStep = Math.min(1.0, sectorSpan / 8);

    if (sectorCrossesBoundary) {
      // Handle boundary crossing
      for (let deg = startDegree; deg < 360; deg += pointStep) {
        const point = this.calculatePointOnOuterEdge(deg, distance);
        if (point) pathPoints.push({ ...point, degree: deg });
      }
      const point360 = this.calculatePointOnOuterEdge(360, distance);
      if (point360) pathPoints.push({ ...point360, degree: 360 });

      for (let deg = 0; deg <= endDegree; deg += pointStep) {
        const point = this.calculatePointOnOuterEdge(deg, distance);
        if (point) pathPoints.push({ ...point, degree: deg });
      }
    } else {
      // Normal case
      for (let deg = startDegree; deg <= endDegree; deg += pointStep) {
        const point = this.calculatePointOnOuterEdge(deg, distance);
        if (point) pathPoints.push({ ...point, degree: deg });
      }
    }

    return pathPoints;
  }

  /**
   * Calculate point on outer edge at given degree
   */
  private calculatePointOnOuterEdge(degree: number, distance: number): { x: number; y: number } | null {
    const containerIndex = this.getContainerIndexForGlobalDegree(degree);
    const localDegree = this.getLocalDegreeFromGlobal(degree);

    const basePosition = this.elementRenderingEngine.calculatePlanetPositionForAnimation(containerIndex, localDegree);
    if (!basePosition) return null;

    return this.calculateOffsetFromCenterLine(containerIndex, basePosition, localDegree, distance);
  }

  /**
   * Create SVG path data from points
   */
  private createPathDataFromPoints(pathPoints: Array<{ x: number; y: number; degree: number }>): string {
    let pathData = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
    for (let i = 1; i < pathPoints.length; i++) {
      pathData += ` L ${pathPoints[i].x} ${pathPoints[i].y}`;
    }
    return pathData;
  }

  /**
   * Create invisible path element for text to follow
   */
  private createInvisiblePath(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    pathId: string,
    pathData: string
  ): void {
    const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathElement.setAttribute('id', pathId);
    pathElement.setAttribute('d', pathData);
    pathElement.setAttribute('fill', 'none');
    pathElement.setAttribute('stroke', 'none');
    pathElement.setAttribute('opacity', '0');
    group.node()!.appendChild(pathElement);
  }

  /**
   * Create curved text element that follows a path
   */
  private createCurvedTextElement(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    pathId: string,
    text: string,
    style: any,
    fontSizeMultiplier: number
  ): void {
    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('font-family', style.fontFamily);
    textElement.setAttribute('font-size', (style.fontSize * fontSizeMultiplier).toString());
    textElement.setAttribute('font-weight', style.fontWeight);
    textElement.setAttribute('fill', style.fill);
    textElement.setAttribute('stroke', style.stroke);
    textElement.setAttribute('stroke-width', style.strokeWidth.toString());
    textElement.setAttribute('class', `${this.chakraType}-curved-label`);

    // Create textPath element
    const textPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
    textPathElement.setAttribute('href', `#${pathId}`);
    textPathElement.setAttribute('startOffset', '50%');
    textPathElement.setAttribute('text-anchor', 'middle');
    textPathElement.textContent = text;

    textElement.appendChild(textPathElement);
    group.node()!.appendChild(textElement);
  }

  /**
   * Subscribe to subdivision toggle events (e.g., Pada for Nakshatra)
   */
  private subscribeToSubdivisionEvents(): void {
    // Use imported globalEventStream directly

    // Subscribe to subdivision toggle events based on chakra type
    if (this.chakraType === 'nakshatra') {
      const subscription = globalEventStream.subscribe('nakshatra.pada.toggle', (event: any) => {
        // Only handle events for this specific chart
        if (event.chartId === this.chartId || event.data?.chartId === this.chartId) {
          const showPada = event.data?.showPada ?? false;
          this.handleSubdivisionToggle(showPada);
        }
      });
      this.eventSubscriptions.push(subscription.unsubscribe);
    }

    // Add more subdivision types as needed (e.g., other chakras with subdivisions)
  }

  /**
   * Handle subdivision toggle by re-rendering with/without subdivisions
   */
  private handleSubdivisionToggle(showSubdivisions: boolean): void {

    if (!this.currentRenderData) {
      devWarn(`⚠️ UniversalChakraRenderer[${this.chakraType}]: No render data available for subdivision toggle`);
      return;
    }

    if (showSubdivisions) {
      // Get ChakraDataService instance and generate subdivision data
      const chakraDataServiceInstance = ChakraDataService.getInstance();


      const subdivisionData = chakraDataServiceInstance.generateSubdivisionData(
        this.chakraType as any,
        this.startPointLongitude,
        true // showLabels
      );


      // Re-render with subdivisions
      const dataWithSubdivisions = {
        ...this.currentRenderData,
        divisionLines: subdivisionData.divisionLines,
        innerLabels: subdivisionData.innerLabels
      };

      this.renderFromData(dataWithSubdivisions);
      // Only show if it was already visible (don't override hide state)
      if (this.isVisible) {
        this.show();
      }
    } else {
      // Re-render without subdivisions (use original data)
      const dataWithoutSubdivisions = {
        ...this.currentRenderData,
        divisionLines: [],
        innerLabels: []
      };

      this.renderFromData(dataWithoutSubdivisions);
      // Only show if it was already visible (don't override hide state)
      if (this.isVisible) {
        this.show();
      }
    }
  }

  /**
   * Cleanup
   */
  public destroy(): void {
    // Unsubscribe from all events
    this.eventSubscriptions.forEach(unsubscribe => unsubscribe());
    this.eventSubscriptions = [];

    this.clear();
    if (this.config.debugMode) {
      devLog(`🗑️ UniversalChakraRenderer[${this.chakraType}]: Destroyed`);
    }
  }
}
